// Utility functions for image transformations using Supabase Storage Transformations
import { isLowDataMode } from "@/utils/networkMonitor";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import { TransformOptions } from "@supabase/storage-js";

// Extend TransformOptions to include our supported formats
interface ImageTransformOptions extends Omit<TransformOptions, 'format'> {
  format?: 'origin' | 'webp' | 'avif' | 'png' | 'jpeg';
}

// Test if an image URL is valid and accessible
export const testImageUrl = async (url: string): Promise<{ ok: boolean; status: number }> => {
  try {
    const response = await fetch(url, { method: 'HEAD' });
    return {
      ok: response.ok,
      status: response.status
    };
  } catch (error) {
    console.error('Error testing image URL:', error);
    return {
      ok: false,
      status: 0
    };
  }
};

// Check if running in development environment
export const isDevEnvironment = () => {
  return window.location.hostname.includes('localhost') || 
         window.location.hostname.includes('127.0.0.1');
};

// Check if running in preview environment
export const isPreviewEnvironment = () => {
  return window.location.hostname.includes('preview') || 
         window.location.hostname.includes('lovable.app');
};

// Function to get image transformation URL
export function getTransformationUrl(
  bucket: string, 
  path: string, 
  width?: number, 
  height?: number, 
  format?: ImageTransformOptions['format'], 
  quality?: number
): string {
  // Get the base URL
  const { data } = supabase.storage.from(bucket).getPublicUrl(path);
  if (!data?.publicUrl) return '';

  // Create transformation options
  const transformOptions: ImageTransformOptions = {};
  if (width) transformOptions.width = width;
  if (height) transformOptions.height = height;
  if (quality) transformOptions.quality = quality;
  if (format) transformOptions.format = format;

  // Get transformed URL
  const { data: transformedData } = supabase.storage
    .from(bucket)
    .getPublicUrl(path, {
      transform: transformOptions as TransformOptions
    });

  return transformedData?.publicUrl || '';
}

// Generate transformation URL using Supabase transformations
export function generateTransformationUrl(url: string, options: {
  width?: number,
  height?: number,
  format?: ImageTransformOptions['format'],
  quality?: number,
  resize?: 'cover' | 'contain' | 'fill'
}): string {
  try {
    // Parse the URL to get bucket and path
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split('/');
    const bucketIndex = pathParts.indexOf('public') + 1;
    
    if (bucketIndex > 0 && bucketIndex < pathParts.length) {
      const bucket = pathParts[bucketIndex];
      const filePath = pathParts.slice(bucketIndex + 1).join('/');
      
      // Get transformed URL using storage client
      const { data } = supabase.storage
        .from(bucket)
        .getPublicUrl(filePath, {
          transform: options as TransformOptions
        });
        
      return data?.publicUrl || url;
    }
    
    return url;
  } catch (error) {
    console.error('Error generating transformation URL:', error);
    return url;
  }
}

// Alias for backward compatibility - Enhanced for safety
export const getDebugImageInfo = (originalUrl: string | null | undefined, transformedUrl: string | null | undefined) => {
  // Safety check: ensure inputs are valid strings
  if (!originalUrl || typeof originalUrl !== 'string') {
    originalUrl = '(no original URL)';
  }
  
  if (!transformedUrl || typeof transformedUrl !== 'string') {
    transformedUrl = '(no transformed URL)';
  }
  
  // Create safe string representations with truncation
  const safeOriginal = String(originalUrl).substring(0, 50) + (originalUrl.length > 50 ? '...' : '');
  const safeTransformed = String(transformedUrl).substring(0, 50) + (transformedUrl.length > 50 ? '...' : '');
  
  // Return a properly formatted debug string
  return `Original: ${safeOriginal}\nTransformed: ${safeTransformed}`;
};

// Check if image URL is from Supabase
export const isSupabaseUrl = (url: string) => {
  return url && url.includes('/storage/v1/object/public/');
};

// Verify that a transformation works
export const verifyImageTransformation = async (originalUrl: string, transformedUrl: string) => {
  if (!transformedUrl) return false;
  
  try {
    const response = await fetch(transformedUrl, { method: 'HEAD' });
    return response.ok;
  } catch (error) {
    console.error('Error verifying transformation:', error);
    return false;
  }
};

// Log image details for debugging
export const logImageDetails = (id: string, originalUrl: string | null, transformedUrl: string | null) => {
  console.log(`[ImageTransform:${id}]`, {
    original: originalUrl ? originalUrl.substring(0, 50) + '...' : 'none',
    transformed: transformedUrl ? transformedUrl.substring(0, 50) + '...' : 'none',
  });
};

// Get transformation options based on context
export const getImageTransformOptions = (
  context: 'card' | 'detail' | 'thumbnail' | 'preview',
  isMobile: boolean,
  isLowData: boolean
): ImageTransformOptions => {
  // Base quality settings
  const quality = isLowData ? 60 : isMobile ? 75 : 85;
  
  // Width based on context and device
  let width = 600; // Default
  
  switch (context) {
    case 'card':
      width = isMobile ? 400 : 600;
      break;
    case 'detail':
      width = isMobile ? 800 : 1200;
      break;
    case 'thumbnail':
      width = 200;
      break;
    case 'preview':
      width = 100;
      break;
  }
  
  // Adjust for low data mode
  if (isLowData) {
    width = Math.floor(width * 0.75); // 25% smaller for low data
  }
  
  return {
    width,
    quality,
    format: 'webp'
  };
};
